/**
 * Version & maintenance interceptor logic (phase C.5).
 *
 * The version middleware on the backend is the single source of truth for
 * compatibility (Phase C rule). It answers a handful of HTTP statuses that the
 * frontend must react to globally — block the app, or pause writes and warn:
 *
 *   426 Upgrade Required  → the running client is unsupported (force update).
 *   423 Locked            → maintenance, read-only window.
 *   503 Service Unavailable → maintenance, full outage (honors `Retry-After`).
 *   400 + version_headers_* → the client sent missing/invalid version headers
 *                             (a client bug — report it, never show the user).
 *
 * This module is framework-agnostic plumbing, mirroring the existing
 * `authSessionExpiredEvent` pattern in `httpClient`: the HTTP layer cannot touch
 * React state, so it derives a `CompatibilitySignal` and dispatches it as a DOM
 * event. The React `AppCompatibilityProvider` (the only subscriber) owns the
 * resulting global state and UI. Keeping the *derivation* pure (see
 * `deriveCompatibilitySignal`) makes the status/code → action mapping unit-testable
 * without a DOM or a provider.
 *
 * Mapping is by `status` + machine `code` only — the backend's human `message`
 * is never matched here; the UI localizes copy per reason/mode instead.
 */
import type { ApiError } from '@/api/ApiError';
import { reportClientError } from '@/api/reportClientError';
// Type-only import: the FE-side reason union lives with the screens it drives
// (phase C.4). Importing the *type* keeps the api layer free of any runtime edge
// back into the feature layer.
import type { ForceUpdateReason } from '@features/appVersion/types';

/** Maintenance posture surfaced to the user as a banner (not a hard block). */
export type MaintenanceLevel = 'read_only' | 'full';

/**
 * A global condition the app must react to. `force-update` hard-blocks the UI;
 * `maintenance` pauses the sync queue and shows a banner while reads continue.
 */
export type CompatibilitySignal =
  | { kind: 'force-update'; reason: ForceUpdateReason }
  | { kind: 'maintenance'; level: MaintenanceLevel; retryAfterSeconds: number | null };

/** 400 error codes that mean the *client* failed the version-header contract. */
const VERSION_HEADER_ERROR_CODES = new Set(['version_headers_required', 'version_headers_invalid']);

export const appCompatibilityEvent = 'fuelops:app-compatibility';
/**
 * Fired on any successful (2xx) request to a version-enforced endpoint. The
 * provider listens only while maintenance is active, using it to clear the
 * banner the moment the backend recovers. The version-check endpoint is excluded
 * by the caller because it is fail-open (a 2xx there does not prove recovery).
 */
export const appHealthyEvent = 'fuelops:app-healthy';

/**
 * Parses an HTTP `Retry-After` header into whole seconds. Supports the two RFC
 * forms (delta-seconds and an HTTP-date) and returns `null` when absent/unpar, or
 * `0` when a date is already in the past.
 */
export const parseRetryAfter = (response: Response): number | null => {
  const raw = response.headers.get('Retry-After');

  if (!raw) {
    return null;
  }

  const asSeconds = Number(raw);
  if (Number.isFinite(asSeconds)) {
    return asSeconds > 0 ? Math.floor(asSeconds) : 0;
  }

  const asDateMs = Date.parse(raw);
  if (!Number.isNaN(asDateMs)) {
    const deltaSeconds = Math.ceil((asDateMs - Date.now()) / 1000);
    return deltaSeconds > 0 ? deltaSeconds : 0;
  }

  return null;
};

/**
 * Pure status/code → signal mapping. Returns `null` for everything the global
 * interceptor should ignore (ordinary 4xx/5xx the caller handles itself). A 426
 * with an unrecognized code defaults to `app_outdated`, the safe reading of
 * "upgrade required".
 */
export const deriveCompatibilitySignal = (error: ApiError): CompatibilitySignal | null => {
  switch (error.status) {
    case 426:
      return {
        kind: 'force-update',
        reason:
          error.code === 'sync_schema_version_unsupported' ? 'sync_schema_outdated' : 'app_outdated'
      };
    case 423:
      return {
        kind: 'maintenance',
        level: 'read_only',
        retryAfterSeconds: parseRetryAfter(error.response)
      };
    case 503:
      return {
        kind: 'maintenance',
        level: 'full',
        retryAfterSeconds: parseRetryAfter(error.response)
      };
    default:
      return null;
  }
};

/** Dispatches a compatibility signal for the React provider to act on. */
export const dispatchCompatibilitySignal = (signal: CompatibilitySignal): void => {
  window.dispatchEvent(
    new CustomEvent<CompatibilitySignal>(appCompatibilityEvent, { detail: signal })
  );
};

/** Subscribes to compatibility signals. Returns an unsubscribe function. */
export const subscribeToCompatibility = (
  handler: (signal: CompatibilitySignal) => void
): (() => void) => {
  const listener = (event: Event) => handler((event as CustomEvent<CompatibilitySignal>).detail);
  window.addEventListener(appCompatibilityEvent, listener);
  return () => window.removeEventListener(appCompatibilityEvent, listener);
};

/** Subscribes to the "backend healthy again" signal. Returns an unsubscribe function. */
export const subscribeToHealthy = (handler: () => void): (() => void) => {
  window.addEventListener(appHealthyEvent, handler);
  return () => window.removeEventListener(appHealthyEvent, handler);
};

/** Notifies subscribers that a version-enforced request just succeeded. */
export const notifyHealthyResponse = (): void => {
  window.dispatchEvent(new Event(appHealthyEvent));
};

/**
 * Interceptor entry point called by `httpClient` for every failed request.
 * Side-effecting: reports client version-header bugs (no UI) and dispatches a
 * compatibility signal for blocking conditions. Returns the derived signal (or
 * `null`) for tests/diagnostics.
 */
export const reportCompatibilityError = (error: ApiError): CompatibilitySignal | null => {
  if (error.status === 400 && error.code && VERSION_HEADER_ERROR_CODES.has(error.code)) {
    reportClientError({
      kind: 'version-headers',
      status: error.status,
      code: error.code,
      message: error.message,
      details: error.details
    });
    return null;
  }

  const signal = deriveCompatibilitySignal(error);
  if (signal) {
    dispatchCompatibilitySignal(signal);
  }

  return signal;
};
